import { useRef, useState } from 'preact/hooks';
import {
  BLANK,
  BLANK_TEMPLATES,
  HEADLINE_MAX,
  HEADLINE_MIN,
  KICKERS,
  SUBTITLE_MAX,
  fillName,
  inventRumor,
  pick,
  type ClientView,
  type ConfessionSource,
  type PublicPlayer,
  type RumorDraft,
  type SlotKind,
} from '@gazette/shared';
import { listNames, locale, t, type MessageKey } from '../../i18n/index.ts';
import { actions } from '../../net/client.ts';
import { ErrorNotice, GameHeader } from '../components/Chrome.tsx';
import { Headline } from '../components/Clipping.tsx';
import { Icon } from '../components/Icon.tsx';
import { Portrait } from '../components/Portrait.tsx';
import { Progress } from '../components/Progress.tsx';
import { useAutosave, type PhaseOf } from '../hooks.ts';

function victimOf(kind: SlotKind, draft: RumorDraft): string | null {
  return kind === 'true' ? (draft.source?.playerId ?? null) : draft.targetId;
}

function problem(kind: SlotKind, draft: RumorDraft): MessageKey | null {
  if (kind === 'true' && !draft.source) return 'write.missing.source';
  if (kind === 'false' && !draft.targetId) return 'write.missing.victim';
  if (draft.headline.trim().length < HEADLINE_MIN) return 'write.missing.headline';
  return null;
}

function Evidence({
  sources,
  players,
  selectable,
  selected,
  locked,
  onUse,
}: {
  sources: ConfessionSource[];
  players: PublicPlayer[];
  selectable: boolean;
  selected: RumorDraft['source'];
  locked: boolean;
  onUse: (source: NonNullable<RumorDraft['source']>) => void;
}) {
  return (
    <div class="evidence">
      {sources.map((source) => {
        const player = players.find((candidate) => candidate.id === source.playerId);
        if (!player) return null;
        return (
          <article class="evidence__card" key={source.playerId}>
            <header class="evidence__header">
              <div class="stamped-portrait">
                <Portrait seed={player.portrait} width={52} />
                <span class="violet-stamp stamped-portrait__stamp">
                  {t('fiche.number', { n: String(players.indexOf(player) + 1).padStart(2, '0') })}
                </span>
              </div>
              <h3>{player.name}</h3>
            </header>
            <ul class="evidence__items">
              {source.items.map((item) => {
                const chosen = selected?.playerId === source.playerId && selected.questionId === item.questionId;
                const body = (
                  <>
                    <span class="evidence__question">{item.question[locale.value]}</span>
                    <span class="evidence__answer">{item.answer}</span>
                  </>
                );
                return (
                  <li>
                    {selectable ? (
                      <button
                        type="button"
                        class={`evidence__item ${chosen ? 'is-chosen' : ''}`}
                        aria-pressed={chosen}
                        disabled={locked}
                        onClick={() => onUse({ playerId: source.playerId, questionId: item.questionId })}
                      >
                        {body}
                        <span class="evidence__use">
                          {chosen && <Icon name="check" size={16} />}
                          {chosen ? t('write.used') : t('write.use')}
                        </span>
                      </button>
                    ) : (
                      <div class="evidence__item">{body}</div>
                    )}
                  </li>
                );
              })}
            </ul>
          </article>
        );
      })}
    </div>
  );
}

function RumorEditor({
  kind,
  draft,
  others,
  players,
  locked,
  onChange,
}: {
  kind: SlotKind;
  draft: RumorDraft;
  others: PublicPlayer[];
  players: PublicPlayer[];
  locked: boolean;
  onChange: (draft: RumorDraft) => void;
}) {
  const headlineRef = useRef<HTMLTextAreaElement>(null);
  const victim = players.find((player) => player.id === victimOf(kind, draft));
  const issue = problem(kind, draft);
  const titleKey = kind === 'true' ? 'write.true.title' : 'write.false.title';

  const applyTemplate = (template: string) => {
    const headline = fillName(template, victim?.name ?? '…');
    onChange({ ...draft, headline });
    requestAnimationFrame(() => {
      const field = headlineRef.current;
      if (!field) return;
      field.focus();
      const blank = headline.indexOf(BLANK);
      if (blank >= 0) field.setSelectionRange(blank, blank + BLANK.length);
    });
  };

  const invent = () => {
    const target = victim ?? pick(Math.random, others);
    const { headline, subtitle } = inventRumor(locale.value, target.name);
    onChange({ ...draft, targetId: target.id, headline, subtitle });
  };

  return (
    <fieldset class={`rumor rumor--${kind}`} disabled={locked}>
      <legend class="rumor__title">{t(titleKey)}</legend>
      <p class="rumor__help">{t(kind === 'true' ? 'write.true.help' : 'write.false.help')}</p>

      {kind === 'false' ? (
        <div class="victim-picker" role="group" aria-label={t('write.victim')}>
          <span class="field__label">{t('write.victim')}</span>
          <div class="victim-picker__list">
            {others.map((player) => (
              <button type="button" class="victim-chip" aria-pressed={draft.targetId === player.id} onClick={() => onChange({ ...draft, targetId: player.id })}>
                <Portrait seed={player.portrait} width={30} />
                {player.name}
              </button>
            ))}
          </div>
        </div>
      ) : (
        victim && (
          <p class="rumor__victim">
            <span class="field__label">{t('write.victim')}</span>
            <Portrait seed={victim.portrait} width={30} />
            {victim.name}
          </p>
        )
      )}

      <div class="kicker-picker" role="group" aria-label={t('write.kicker')}>
        <span class="field__label">{t('write.kicker')}</span>
        <div class="kicker-picker__list">
          {KICKERS.map((kicker) => (
            <button type="button" class="chip" aria-pressed={draft.kicker === kicker} onClick={() => onChange({ ...draft, kicker })}>
              {t(`kicker.${kicker}`)}
            </button>
          ))}
        </div>
      </div>

      <label class="field">
        <span class="field__label">
          {t('write.headline')}
          <span class="field__count">{t('write.chars', { n: draft.headline.length, max: HEADLINE_MAX })}</span>
        </span>
        <textarea
          ref={headlineRef}
          class="typed typed--ruled typed--headline"
          rows={3}
          maxLength={HEADLINE_MAX}
          value={draft.headline}
          placeholder={t('write.headline.placeholder')}
          onInput={(event) => onChange({ ...draft, headline: event.currentTarget.value })}
        />
      </label>

      <div class="templates" role="group" aria-label={t('write.templates')}>
        <span class="field__label">{t('write.templates')}</span>
        <div class="templates__list">
          {BLANK_TEMPLATES[locale.value].map((template) => (
            <button type="button" class="chip chip--template" onClick={() => applyTemplate(template)}>
              {fillName(template, victim?.name ?? '…')}
            </button>
          ))}
        </div>
      </div>

      {kind === 'false' && (
        <button type="button" class="btn btn--secondary btn--small btn--on-manila" onClick={invent}>
          <Icon name="quill" size={18} />
          {t('write.invent')}
        </button>
      )}

      <label class="field">
        <span class="field__label">{t('write.subtitle')}</span>
        <input
          class="typed"
          maxLength={SUBTITLE_MAX}
          value={draft.subtitle}
          placeholder={t('write.subtitle.placeholder')}
          onInput={(event) => onChange({ ...draft, subtitle: event.currentTarget.value })}
        />
      </label>

      <div class="preview" aria-hidden="true">
        <p class="preview__headline">
          <Headline kicker={draft.kicker} headline={draft.headline || '…'} />
        </p>
        {draft.subtitle && <p class="preview__sub">{draft.subtitle}</p>}
      </div>
      {issue && <p class="hint hint--ink">{t(issue)}</p>}
    </fieldset>
  );
}

export function Writing({ current, phase }: { current: ClientView; phase: PhaseOf<'writing'> }) {
  const [drafts, setDrafts] = useState<RumorDraft[]>(() => phase.drafts);
  const [sent, setSent] = useState(phase.done);
  const others = current.players.filter((player) => player.id !== current.meId);
  const trueSlot = phase.slots.indexOf('true');
  const ready = phase.slots.every((kind, index) => problem(kind, drafts[index]!) === null);
  const sourceNames = phase.sources.map((source) => current.players.find((player) => player.id === source.playerId)?.name ?? '?');

  const cancelAutosave = useAutosave(drafts, (value) => void actions.writing(value, sent));

  const update = (index: number, draft: RumorDraft) => {
    setDrafts((previous) => previous.map((current, position) => (position === index ? draft : current)));
  };

  const send = (done: boolean) => {
    cancelAutosave();
    setSent(done);
    void actions.writing(drafts, done);
  };

  return (
    <>
      <GameHeader />
      <section class="sheet sheet--full" data-occlude aria-label={t('phase.writing')}>
        {phase.corbeau && (
          <aside class="corbeau-note">
            <h2>{t('write.corbeau.title')}</h2>
            <p>{t('write.corbeau.body')}</p>
          </aside>
        )}
        <p class="sheet__intro">{phase.sources.length > 0 ? t('write.intro', { names: listNames(sourceNames) }) : t('write.noSources')}</p>

        {phase.sources.length > 0 && (
          <>
            <h2 class="section-title">{t('write.sources')}</h2>
            <Evidence
              sources={phase.sources}
              players={current.players}
              selectable={trueSlot >= 0}
              selected={trueSlot >= 0 ? drafts[trueSlot]!.source : null}
              locked={sent}
              onUse={(source) => update(trueSlot, { ...drafts[trueSlot]!, source })}
            />
          </>
        )}

        <form
          class="fiche-form fiche-form--rumors"
          onSubmit={(event) => {
            event.preventDefault();
            if (ready) send(true);
          }}
        >
          {phase.slots.map((kind, index) => (
            <RumorEditor
              kind={kind}
              draft={drafts[index]!}
              others={others}
              players={current.players}
              locked={sent}
              onChange={(draft) => update(index, draft)}
            />
          ))}
          <div class="fiche-form__actions">
            {sent ? (
              <>
                <p class="violet-stamp violet-stamp--large" role="status">
                  {t('write.sent')}
                </p>
                <button type="button" class="btn btn--link" onClick={() => send(false)}>
                  {t('write.edit')}
                </button>
              </>
            ) : (
              <button type="submit" class="btn btn--ink btn--wide" disabled={!ready}>
                {t('write.send')}
              </button>
            )}
          </div>
        </form>
        <ErrorNotice />
        <Progress progress={phase.progress} label={t('progress.sent', phase.progress)} />
      </section>
    </>
  );
}
